import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, ButtonGroup, Col, Form, Row } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import AbpnSpinner from "../../Layout/Components/AbpnSpinner";
import { abpnClient } from "../../Services/AbpnClient";
import { ApprovedActivitiesSearchResultSet } from "../Models/ApprovedActivity";
import { Certification } from "../Models/Certification";
import ActivitiesTable from "./ActivitiesTable";
import ResultFilters from "./ResultFilters";

const filterParamNames = [
  "CertificationIds",
  "IncludeAllCertificationsActivities",
  "OrganizationFilter",
  "ProductFilter",
  "CertificationFilter",
  "CmeFilter",
  "SaFilter",
  "CmeCreditsValue",
  "CmeCreditsEndValue",
  "SaCreditsValue",
  "SaCreditsEndValue",
  "PipClinicalFilter",
  "PipFeedbackFilter",
  "PatientSafetyFilter",
  "EventStartDateValue",
  "EventStartDateEndValue",
  "EventEndDateValue",
  "EventEndDateEndValue"
];

export default function ActivitiesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [resultSet, setResultSet] = useState<ApprovedActivitiesSearchResultSet>(null);
  const [loadingCertifications, setLoadingCertifications] = useState<boolean>(true);
  const [loadingResults, setLoadingResults] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const resultsRegionRef = useRef<HTMLDivElement>(null);
  const pendingPageFocusRef = useRef<number | null>(null);

  const selectedCertificationIds = useMemo(() => {
    return searchParams.getAll("CertificationIds").map(_ => parseInt(_)).filter(_ => !isNaN(_));
  }, [searchParams]);

  const includeAll = searchParams.get("IncludeAllCertificationsActivities") === "true";
  const hasCertificationFilter = selectedCertificationIds.length > 0 || includeAll;
  const requestedPageNumber = parseInt(searchParams.get("PageNumber") ?? "1");
  const pageNumber = isNaN(requestedPageNumber) ? 1 : requestedPageNumber;
  const totalPages = resultSet?.totalCount ? Math.ceil(resultSet.totalCount / resultSet.pageSize) : 0;
  const activeFilterCount = filterParamNames.filter(name => searchParams.has(name) && searchParams.get(name)).length;

  useEffect(() => {
    const loadCertifications = async () => {
      setLoadingCertifications(true);
      const data = await abpnClient.get<Certification[]>("/api/activities/certifications");
      setCertifications(data);
      setLoadingCertifications(false);
    };

    loadCertifications().catch(_ => setError("Unable to load certifications."));
  }, []);

  useEffect(() => {
    if (!hasCertificationFilter) {
      setResultSet(null);
      return;
    }

    const loadResults = async () => {
      setLoadingResults(true);
      setError("");
      const query = new URLSearchParams(searchParams);
      if (!query.has("PageNumber")) query.set("PageNumber", "1");
      if (!query.has("PageSize")) query.set("PageSize", "25");
      const data = await abpnClient.get<ApprovedActivitiesSearchResultSet>(`/api/activities/search?${query.toString()}`);
      setResultSet(data);
      setLoadingResults(false);
    };

    loadResults().catch(_ => {
      setLoadingResults(false);
      setError("Unable to load approved activities.");
    });
  }, [searchParams, hasCertificationFilter]);

  useEffect(() => {
    if (pendingPageFocusRef.current === null || loadingResults || !resultSet) return;
    if (resultSet.pageNumber !== pendingPageFocusRef.current) return;

    pendingPageFocusRef.current = null;
    resultsRegionRef.current?.focus({ preventScroll: true });
    resultsRegionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [loadingResults, resultSet]);

  const updateCertificationSelection = (certificationIds: number[]) => {
    const next = new URLSearchParams(searchParams);
    next.delete("CertificationIds");
    certificationIds.forEach(id => next.append("CertificationIds", id.toString()));
    next.set("PageNumber", "1");
    setSearchParams(next);
  };

  const updateIncludeAll = (value: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("IncludeAllCertificationsActivities", "true");
    else next.delete("IncludeAllCertificationsActivities");
    next.set("PageNumber", "1");
    setSearchParams(next);
  };

  const updateProductSearch = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("ProductFilter", value);
    else next.delete("ProductFilter");
    next.set("PageNumber", "1");
    setSearchParams(next);
  };

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    filterParamNames.forEach(name => next.delete(name));
    [
      "CmeCreditsOperator",
      "SaCreditsOperator",
      "EventStartDateOperator",
      "EventEndDateOperator"
    ].forEach(name => next.delete(name));
    next.set("PageNumber", "1");
    setSearchParams(next);
  };

  const updatePage = (page: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("PageNumber", page.toString());
    pendingPageFocusRef.current = page;
    setSearchParams(next);
  };

  return (
    <main className="mt-4">
      <section className="aa-page-shell">
        <h1 className="aa-page-title">Approved CC Activities List</h1>
        <p className="text-primary fw-bold mb-4">Continuing Certification Activities</p>

        {error && <Alert variant="danger" role="alert">{error}</Alert>}

        <Row className="g-4">
          <Col xs={12} lg={3}>
            {loadingCertifications ? <AbpnSpinner /> :
              <ResultFilters
                certifications={certifications}
                selectedCertificationIds={selectedCertificationIds}
                includeAll={includeAll}
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                onCertificationChange={updateCertificationSelection}
                onIncludeAllChange={updateIncludeAll}
                onClearFilters={clearFilters}
                clearDisabled={activeFilterCount === 0}
              />
            }
          </Col>
          <Col xs={12} lg={9}>
            {!hasCertificationFilter &&
              <Alert variant="info" role="status" aria-live="polite">
                Please select a specialty and/or subspecialty.
              </Alert>
            }

            {hasCertificationFilter &&
              <section aria-live="polite">
                <Row className="align-items-end mb-3 g-3">
                  <Col xs={12} md={6}>
                    <Form.Group controlId="ProductFilter">
                      <Form.Label className="fw-bold text-secondary">Search Activity Name</Form.Label>
                      <Form.Control
                        type="search"
                        value={searchParams.get("ProductFilter") ?? ""}
                        onChange={e => updateProductSearch(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6} className="text-md-end">
                    <span className="fw-bold text-secondary">
                      {resultSet ? `${resultSet.totalCount.toLocaleString()} result${resultSet.totalCount === 1 ? "" : "s"}` : "Results"}
                    </span>
                    {activeFilterCount > 0 && <span className="ms-2 text-muted">({activeFilterCount} active filter{activeFilterCount === 1 ? "" : "s"})</span>}
                  </Col>
                </Row>

                {loadingResults && <AbpnSpinner />}

                {!loadingResults && resultSet?.results?.length === 0 &&
                  <Alert variant="info" role="status">There are no approved activities for this selection.</Alert>
                }

                {!loadingResults && resultSet?.results?.length > 0 &&
                  <div ref={resultsRegionRef} role="region" tabIndex={-1} aria-label="Approved activities results">
                    <PaginationControls
                      currentPage={resultSet.pageNumber || pageNumber}
                      totalPages={totalPages}
                      onPageChange={updatePage}
                      className="mb-3"
                    />
                    <ActivitiesTable
                      activities={resultSet.results}
                      searchParams={searchParams}
                      setSearchParams={setSearchParams}
                    />
                    <PaginationControls
                      currentPage={resultSet.pageNumber || pageNumber}
                      totalPages={totalPages}
                      onPageChange={updatePage}
                      className="mt-3"
                    />
                  </div>
                }
              </section>
            }
          </Col>
        </Row>
      </section>
    </main>
  );
}

function PaginationControls(props: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}) {
  const currentPage = props.currentPage || 1;

  return (
    <div className={`d-flex justify-content-between align-items-center ${props.className ?? ""}`}>
      <span className="aa-font-size-smallest text-muted">
        Page {currentPage} of {props.totalPages}
      </span>
      <ButtonGroup aria-label="Pagination">
        <Button variant="outline-secondary" size="sm" disabled={currentPage <= 1} onClick={() => props.onPageChange(currentPage - 1)}>
          Previous
        </Button>
        <Button variant="outline-secondary" size="sm" disabled={!props.totalPages || currentPage >= props.totalPages} onClick={() => props.onPageChange(currentPage + 1)}>
          Next
        </Button>
      </ButtonGroup>
    </div>
  );
}
